import type { NotUndefined } from 'object-hash';

import { normalizeDocument } from '@gitbook/doc-core';
import type { Document } from '@gitbook/slate';
import { createHTTPError, type DebugLogger, Logger } from '@gitbook/util-debug';
import { type MaybePromise, maybePromise } from '@gitbook/util-ts';

import {
    getPageParentsIndexInRevision,
    type RevisionPageParentIndex,
    RootParentSymbol,
} from '../revisions';
import {
    asPageWithDescendants,
    type ComputedContentSourceDocument,
    type ComputedContentSourceRevision,
    type DefaultComputedContentSource,
    type Revision,
    RevisionPageType,
    RevisionType,
} from '../types';
import { applyRevisionComputations } from './revision';
import {
    extractComputedContentSubsetState,
    fillComputedContentSource,
    getComputedSourceResultId,
} from './sources';
import type {
    ComputedContentDocumentResult,
    ComputedContentRevisionLayerResult,
    ComputedContentSourceDependency,
    ComputedContentSourceDependencyValue,
    ComputedContentSourceResolved,
    ContentComputerState,
    ContentComputerSubsetState,
    RevisionComputations,
} from './types';
import { hash, hashDependencyRef } from './utils';

const MAX_RECURSION_DEPTH = 10;

export type RunComputeRevisionResult = {
    contentId: Revision['contentId'];
    files?: Revision['files'];
    reusableContents?: Revision['reusableContents'];
    variables?: Revision['variables'];
} & (
    | {
          pages: Revision['pages'];
          rootPages: Revision['rootPages'];
      }
    | {
          pages?: undefined;
          rootPages?: undefined;
      }
);

export type ContentComputerContext<
    SourceRevision extends DefaultComputedContentSource = ComputedContentSourceRevision,
    SourceDocument extends DefaultComputedContentSource = ComputedContentSourceDocument,
> = {
    /**
     * Get the version of a source type.
     * It is used to determine if the source has changed.
     */
    getSourceVersion: (
        sourceType: SourceRevision['type'] | SourceDocument['type']
    ) => Promise<string>;

    /**
     * Resolve a dependency to its latest value.
     */
    resolveDependency: (
        dependency: ComputedContentSourceDependency<SourceRevision | SourceDocument>['ref'],
        onUpdate: (
            value: ComputedContentSourceDependencyValue<
                ComputedContentSourceDependency<SourceRevision | SourceDocument>
            >
        ) => void,
        onError?: (error: Error) => void
    ) => () => void;

    /**
     * Compute the set of pages for a given source.
     */
    runComputeRevision: (input: {
        computedSeed: string;
        computedSource: ComputedContentSourceResolved<SourceRevision>;
        /** Unique identifier the computation, it can be as an Idempotency key */
        computationId: string;
    }) => MaybePromise<RunComputeRevisionResult>;

    /**
     * Compute the document for a given source.
     */
    runComputeDocument: (input: {
        computedSeed: string;
        computedSource: ComputedContentSourceResolved<SourceDocument>;
        /** Unique identifier the computation, it can be as an Idempotency key */
        computationId: string;
    }) => MaybePromise<Document>;

    /**
     * Wait until a promise is resolved.
     * This is used to ensure control over the async operations when executed in an environment like Cloudflare.
     */
    waitUntil?: (promise: Promise<void>) => void;
};

/**
 * The store interface represents how the content computer can persist results.
 */
export type ContentComputerStore = {
    /**
     * Get a document by its ID.
     */
    getDocument: (documentId: string) => MaybePromise<Document | undefined>;

    /**
     * Save a document.
     */
    setDocument: (documentId: string, document: Document) => MaybePromise<void>;

    /**
     * Get a revision by its ID.
     */
    getRevision: (revisionId: string) => MaybePromise<Revision | undefined>;

    /**
     * Save a revision.
     */
    setRevision: (revision: Revision) => MaybePromise<void>;
};

export class ContentComputer<
    SourceRevision extends DefaultComputedContentSource = ComputedContentSourceRevision,
    SourceDocument extends DefaultComputedContentSource = ComputedContentSourceDocument,
> {
    public context: ContentComputerContext<SourceRevision, SourceDocument>;
    public store: ContentComputerStore;
    private state: ContentComputerState = {
        types: {},
        dependencies: {},
    };

    private revisionLayersMemoryCache: Map<string, ComputedContentRevisionLayerResult> = new Map();
    private documentsMemoryCache: Map<string, ComputedContentDocumentResult> = new Map();

    private dependencySubscriptions: Map<
        string,
        {
            callbacks: Set<() => void>;
            cancel: () => void;
        }
    > = new Map();

    private logger: DebugLogger;

    private pending: Array<Promise<void>> = [];

    constructor({
        context,
        store,
        logger,
    }: {
        context: ContentComputerContext<SourceRevision, SourceDocument>;
        store: ContentComputerStore;
        logger?: DebugLogger;
    }) {
        this.context = context;
        this.store = store;
        this.logger = logger ?? Logger('hive:compute');
    }

    /**
     * Reset the whole computer.
     */
    public reset() {
        this.revisionLayersMemoryCache = new Map();
        this.documentsMemoryCache = new Map();
        this.state = {
            types: {},
            dependencies: {},
        };
    }

    /**
     * Get a complete computed document.
     */
    public getCompleteDocument(input: {
        computedSeed: string;
        computedSource: SourceDocument;
    }): MaybePromise<{
        document: Document;
        documentId: string;
    }> {
        return maybePromise((resolve, reject) => {
            const handleResult = (result: ComputedContentDocumentResult) => {
                if (result.complete && result.result) {
                    resolve(result.result);
                    cancel();
                } else if (result.error) {
                    reject(result.error);
                    cancel();
                }
            };

            const { result, cancel } = this.computeDocument({
                input,
                onUpdate: handleResult,
            });

            handleResult(result);
        });
    }

    /**
     * Get a complete computed document, always returning a promise.
     */
    public async getCompleteDocumentAsync(input: {
        computedSeed: string;
        computedSource: SourceDocument;
    }) {
        const result = await this.getCompleteDocument(input);
        return result;
    }

    /**
     * Compute a document and be notified of its changes.
     */
    public computeDocument({
        input,
        onUpdate,
    }: {
        input: {
            computedSeed: string;
            computedSource: SourceDocument;
        };
        onUpdate: (result: ComputedContentDocumentResult) => void;
    }): {
        result: ComputedContentDocumentResult;
        cancel: () => void;
    } {
        const { computedSeed, computedSource } = input;
        let cancelled = false;

        const getResultForState = (
            state: ContentComputerSubsetState
        ): ComputedContentDocumentResult => {
            if (!state.complete) {
                return {
                    result: null,
                    complete: false,
                    error: null,
                };
            }

            const documentId = getComputedSourceResultId({
                source: computedSource,
                seed: computedSeed,
                state,
            });

            const memoryCachedDocument = this.documentsMemoryCache.get(documentId);
            if (memoryCachedDocument) {
                return memoryCachedDocument;
            }

            this.waitUntil(async () => {
                const storedDocument = await this.store.getDocument(documentId);
                if (storedDocument) {
                    const response: ComputedContentDocumentResult = {
                        result: {
                            document: storedDocument,
                            documentId,
                        },
                        complete: true,
                        error: null,
                    };
                    this.documentsMemoryCache.set(documentId, response);
                    if (!cancelled) {
                        onUpdate(response);
                    }

                    return;
                }

                let response: ComputedContentDocumentResult = {
                    result: null,
                    complete: false,
                    error: null,
                };

                try {
                    const rawComputedDocument = await this.context.runComputeDocument({
                        computedSeed,
                        computedSource: fillComputedContentSource(computedSource, state),
                        computationId: documentId,
                    });

                    const normalizedDocument = normalizeDocument(rawComputedDocument);

                    response = {
                        result: {
                            document: normalizedDocument,
                            documentId,
                        },
                        complete: true,
                        error: null,
                    };
                } catch (error) {
                    this.logger.error(`Failed to compute document ${documentId}`, error);
                    response = {
                        result: null,
                        complete: true,
                        error: error as Error,
                    };
                }

                this.documentsMemoryCache.set(documentId, response);

                if (!cancelled) {
                    onUpdate(response);

                    return;
                }
            });

            return {
                result: null,
                complete: false,
                error: null,
            };
        };

        const pageState = this.computeSubsetState({
            sources: [input.computedSource],
            onUpdate: (newPageState) => {
                onUpdate(getResultForState(newPageState));
            },
            onError: (error) => {
                onUpdate({
                    result: null,
                    complete: false,
                    error,
                });
            },
        });

        return {
            result: getResultForState(pageState.result),
            cancel: () => {
                cancelled = true;
                pageState.cancel();
            },
        };
    }

    /**
     * Get a complete computed revision.
     */
    public getCompleteRevision(
        sourceRevision: Revision,
        options: {
            /**
             * Throw an error if the revision fails to completly compute.
             * If false, a partial revision will be returned.
             * @default false
             */
            throwOnError?: boolean;
        } = {}
    ): MaybePromise<{
        revision: Revision;
        computations: RevisionComputations;
        error?: Error;
    }> {
        const { throwOnError = false } = options;
        return maybePromise((resolve, reject) => {
            const evaluateComputations = (chunk: RevisionComputations) => {
                const applyResult = applyRevisionComputations({
                    revision: sourceRevision,
                    computations: chunk,
                });

                if (!applyResult.complete) {
                    return;
                }

                if (applyResult.error) {
                    if (throwOnError) {
                        reject(applyResult.error);
                    } else {
                        resolve({
                            revision: applyResult.result,
                            computations: chunk,
                            error: applyResult.error,
                        });
                    }

                    cancel();
                    return;
                }

                resolve({
                    revision: applyResult.result,
                    computations: chunk,
                });
                cancel();
            };

            const { result, cancel } = this.computeRevision({
                revision: sourceRevision,
                onUpdate: evaluateComputations,
            });

            evaluateComputations(result);
        });
    }

    /**
     * Get a complete computed revision, always returning a promise.
     */
    public async getCompleteRevisionAsync(sourceRevision: Revision) {
        const result = await this.getCompleteRevision(sourceRevision);
        return result;
    }

    /**
     * Compute a revision and subscribe to its changes.
     */
    public computeRevision({
        revision,
        onUpdate,
        recursionSafetyHashes = new Set(),
        recursionSafetyDepth = 0,
    }: {
        revision: Revision;

        /** Callback to notify the progress of the computation. */
        onUpdate: (chunk: RevisionComputations) => void;

        /** Safety check to avoid recursive computation. We store the hash of the previous source. */
        recursionSafetyHashes?: Set<string>;
        recursionSafetyDepth?: number;
    }): {
        result: RevisionComputations;
        cancel: () => void;
    } {
        const cancelCallbacks: (() => void)[] = [];

        // Locate all computed pages in the source revision
        const toCompute = Object.values(revision.pages).filter(
            (page) => page.type === RevisionPageType.Computed
        );

        let parentsIndex: RevisionPageParentIndex | undefined;
        let result: RevisionComputations = {
            pages: Object.fromEntries(
                toCompute.map((computedPage) => {
                    parentsIndex = parentsIndex || getPageParentsIndexInRevision(revision);

                    const parentId = parentsIndex.get(computedPage.id);
                    if (!parentId) {
                        throw new Error(`Couldn't find parent of page ${computedPage.id}`);
                    }

                    const pages =
                        parentId === RootParentSymbol
                            ? revision.rootPages
                            : asPageWithDescendants(revision.pages[parentId]).pages;
                    const index = pages.indexOf(computedPage.id);

                    return [
                        computedPage.id,
                        {
                            parentId,
                            computedPage,
                            index,
                            result: null,
                            complete: false,
                            error: null,
                        },
                    ];
                })
            ),
        };

        const updateResultForPage = (pageId: string, layer: ComputedContentRevisionLayerResult) => {
            result = {
                ...result,
                pages: {
                    ...result.pages,
                    [pageId]: {
                        ...result.pages[pageId],
                        ...layer,
                    },
                },
            };

            return result;
        };

        for (const page of toCompute) {
            const pageHash = hash(page.computed);

            if (recursionSafetyDepth > MAX_RECURSION_DEPTH) {
                updateResultForPage(page.id, {
                    result: null,
                    complete: true,
                    error: new createHTTPError.LoopDetected(
                        `Max recursion depth reached at depth ${recursionSafetyDepth} with source ${JSON.stringify(page.computed)}`
                    ),
                });
            } else if (recursionSafetyHashes.has(pageHash)) {
                updateResultForPage(page.id, {
                    result: null,
                    complete: true,
                    error: new createHTTPError.LoopDetected(
                        `Infinite recursion detected at depth ${recursionSafetyDepth} with source ${JSON.stringify(page.computed)}`
                    ),
                });
            } else {
                const pageComputation = this.computeRevisionLayer({
                    computedSeed: page.id,
                    computedSource: page.computed as SourceRevision,
                    onUpdate: (layer) => {
                        onUpdate(updateResultForPage(page.id, layer));
                    },
                    recursionSafetyHashes: new Set([...recursionSafetyHashes, pageHash]),
                    recursionSafetyDepth: recursionSafetyDepth + 1,
                });

                if (pageComputation.result) {
                    updateResultForPage(page.id, pageComputation.result);
                }

                cancelCallbacks.push(pageComputation.cancel);
            }
        }

        return {
            result,
            cancel: () => {
                cancelCallbacks.forEach((cancel) => cancel());
            },
        };
    }

    /**
     * Compute a complete revision layer.
     */
    public getCompleteRevisionLayer(input: {
        computedSeed: string;
        computedSource: SourceRevision;
    }): MaybePromise<Revision> {
        return maybePromise((resolve, reject) => {
            const handleResult = (result: ComputedContentRevisionLayerResult) => {
                if (!result.complete) {
                    return;
                }

                if (result.error) {
                    reject(result.error);
                    cancel();
                } else if (result.result) {
                    resolve(result.result);
                    cancel();
                } else {
                    throw new Error('Invalid state');
                }
            };

            const { result, cancel } = this.computeRevisionLayer({
                computedSeed: input.computedSeed,
                computedSource: input.computedSource,
                onUpdate: handleResult,
            });

            handleResult(result);
        });
    }

    /**
     * Compute a specific revision layer, corresponding to a specific computed page.
     */
    private computeRevisionLayer({
        computedSeed,
        computedSource,
        onUpdate,
        recursionSafetyHashes = new Set(),
        recursionSafetyDepth = 0,
    }: {
        computedSeed: string;
        computedSource: SourceRevision;

        /** Callback to notify with the revision computed */
        onUpdate: (state: ComputedContentRevisionLayerResult) => void;

        /** Safety check to avoid recursive computation. We store the hash of the previous source. */
        recursionSafetyHashes?: Set<string>;
        recursionSafetyDepth?: number;
    }): {
        result: ComputedContentRevisionLayerResult;
        cancel: () => void;
    } {
        let cancelled = false;
        let cancelRecursiveStream: (() => void) | undefined;

        const transformRevisionLayerComputations = (
            revisionLayer: Revision,
            computations: RevisionComputations
        ): ComputedContentRevisionLayerResult => {
            const applyResult = applyRevisionComputations({
                revision: revisionLayer,
                computations,
            });

            if (applyResult.error) {
                return {
                    result: null,
                    complete: true,
                    error: applyResult.error,
                };
            }

            return {
                result: applyResult.result,
                complete: applyResult.complete,
                error: null,
            };
        };

        const transformRevisionLayer = (layer: ComputedContentRevisionLayerResult) => {
            const { result } = layer;
            if (!result) {
                // If the layer is not computed yet, return the layer as is
                return layer;
            }

            // Compute recursively the layer and stream the updates
            const recursiveStream = this.computeRevision({
                revision: result,
                onUpdate: (computations) => {
                    onUpdate(transformRevisionLayerComputations(result, computations));
                },
                recursionSafetyHashes,
                recursionSafetyDepth,
            });

            cancelRecursiveStream = recursiveStream.cancel;
            return transformRevisionLayerComputations(result, recursiveStream.result);
        };

        const getLayerForState = (state: ContentComputerSubsetState) => {
            if (cancelRecursiveStream) {
                cancelRecursiveStream();
                cancelRecursiveStream = undefined;
            }

            if (!state.complete) {
                return transformRevisionLayer({
                    result: null,
                    complete: false,
                    error: null,
                });
            }

            const layerRevisionId = getComputedSourceResultId({
                source: computedSource,
                seed: computedSeed,
                state,
            });

            // Try from the memory cache
            const cachedRevisionLayer = this.revisionLayersMemoryCache.get(layerRevisionId);
            if (cachedRevisionLayer) {
                return transformRevisionLayer(cachedRevisionLayer);
            }

            this.waitUntil(async () => {
                // Try from the store
                const storedRevision = await this.store.getRevision(layerRevisionId);
                if (storedRevision) {
                    this.logger.info(`Found revision ${layerRevisionId} in store`);

                    const storedRevisionLayer: ComputedContentRevisionLayerResult = {
                        result: storedRevision,
                        complete: true,
                        error: null,
                    };
                    this.revisionLayersMemoryCache.set(layerRevisionId, storedRevisionLayer);

                    if (!cancelled) {
                        onUpdate(transformRevisionLayer(storedRevisionLayer));
                    }

                    return;
                }

                // Compute the layer revision
                this.logger.info(`Computing revision ${layerRevisionId}`);

                let computedRevisionLayer: ComputedContentRevisionLayerResult = {
                    result: null,
                    complete: false,
                    error: null,
                };
                try {
                    const result = await this.context.runComputeRevision({
                        computedSeed,
                        computedSource: fillComputedContentSource(computedSource, state),
                        computationId: layerRevisionId,
                    });

                    computedRevisionLayer = {
                        result: {
                            id: layerRevisionId,
                            type: RevisionType.Computed,
                            edits: {},
                            parents: [],
                            contentId: result.contentId,
                            variables: result.variables ?? {},
                            reusableContents: result.reusableContents ?? {},
                            files: result.files ?? {},
                            pages: result.pages ?? {},
                            rootPages: result.rootPages ?? [],
                        },
                        complete: true,
                        error: null,
                    };
                } catch (error) {
                    this.logger.error(`Failed to compute revision ${layerRevisionId}`, error);
                    computedRevisionLayer = {
                        result: null,
                        complete: true,
                        error: error as Error,
                    };
                }

                this.revisionLayersMemoryCache.set(layerRevisionId, computedRevisionLayer);

                if (!cancelled) {
                    onUpdate(transformRevisionLayer(computedRevisionLayer));
                }

                // Save the revision in the store
                if (computedRevisionLayer.result) {
                    try {
                        await this.store.setRevision(computedRevisionLayer.result);
                    } catch (error) {
                        this.logger.error(
                            `Failed to save revision ${layerRevisionId}, ignoring`,
                            error
                        );
                    }
                }
            });

            return transformRevisionLayer({
                result: null,
                complete: false,
                error: null,
            });
        };

        const pageState = this.computeSubsetState({
            sources: [computedSource],
            onUpdate: (newPageState) => {
                onUpdate(getLayerForState(newPageState));
            },
            onError: (error) => {
                onUpdate({
                    result: null,
                    complete: false,
                    error,
                });
            },
        });

        return {
            result: getLayerForState(pageState.result),
            cancel: () => {
                cancelled = true;
                pageState.cancel();
                cancelRecursiveStream?.();
            },
        };
    }

    /**
     * Compute a subset of the state and stream updates as dependencies change.
     */
    private computeSubsetState({
        sources,
        onUpdate,
        onError,
    }: {
        /** Sources to compute. */
        sources: Array<SourceRevision | SourceDocument>;

        /** Callback to notify when the state is updated. */
        onUpdate: (state: ContentComputerSubsetState) => void;
        onError: (error: Error) => void;
    }): {
        result: ContentComputerSubsetState;
        cancel: () => void;
    } {
        const cancelCallbacks: (() => void)[] = [];

        const initial = extractComputedContentSubsetState(this.state, sources);

        const onStateChange = () => {
            const { result } = extractComputedContentSubsetState(this.state, sources);
            onUpdate(result);
        };

        for (const dependency of initial.allDependencies) {
            cancelCallbacks.push(
                this.subscribeToDependency({
                    dependency,
                    onUpdate: onStateChange,
                    onError,
                })
            );
        }

        for (const sourceType of initial.sourceTypesToFetch) {
            const { cancel } = this.fetchSourceType({
                sourceType,
                onUpdate: onStateChange,
            });

            cancelCallbacks.push(cancel);
        }

        return {
            result: initial.result,
            cancel: () => {
                cancelCallbacks.forEach((cancel) => cancel());
            },
        };
    }

    /**
     * Subscribe to changes of a dependency.
     */
    private subscribeToDependency({
        dependency,
        onUpdate,
        onError,
    }: {
        dependency: ComputedContentSourceDependency<SourceRevision | SourceDocument>;
        onUpdate: () => void;
        onError: (error: Error) => void;
    }): () => void {
        let subscription: { callbacks: Set<() => void>; cancel: () => void } | undefined;

        try {
            subscription = this.getDependencySubscription(dependency, onError);
            subscription.callbacks.add(onUpdate);
        } catch (error) {
            onError(error as Error);
        }

        return () => {
            if (!subscription) {
                return;
            }

            subscription.callbacks.delete(onUpdate);
            if (subscription.callbacks.size === 0) {
                subscription.cancel();
            }
        };
    }

    /**
     * Fetch and start listening to changes of a dependency.
     */
    private getDependencySubscription(
        dependency: ComputedContentSourceDependency<SourceRevision | SourceDocument>,
        onError: (error: Error) => void
    ) {
        const dependencyHash = hashDependencyRef(dependency);
        const existingSubscription = this.dependencySubscriptions.get(dependencyHash);

        if (existingSubscription) {
            return existingSubscription;
        }

        let cancelled = false;

        const subscription = {
            callbacks: new Set<() => void>(),
            cancel: () => {
                cancelled = true;
                this.dependencySubscriptions.delete(dependencyHash);
                if (cancelResolve) {
                    cancelResolve();
                }
            },
        };
        this.dependencySubscriptions.set(dependencyHash, subscription);

        const onValue = (
            value: ComputedContentSourceDependencyValue<
                ComputedContentSourceDependency<SourceRevision | SourceDocument>
            >
        ) => {
            this.state.dependencies[dependencyHash] = {
                versionHash: hash(value as NotUndefined),
                ref: dependency.ref,
                value,
            };

            if (!cancelled) {
                subscription.callbacks.forEach((callback) => callback());
            }
        };

        const cancelResolve = this.context.resolveDependency(dependency.ref, onValue, onError);

        return subscription;
    }

    /**
     * Fetch a source type.
     */
    private fetchSourceType({
        sourceType,
        onUpdate,
    }: {
        sourceType: SourceDocument['type'] | SourceRevision['type'];
        onUpdate: () => void;
    }): {
        cancel: () => void;
    } {
        let cancelled = false;

        this.waitUntil(async () => {
            const version = await this.context.getSourceVersion(sourceType);
            this.state.types[sourceType] = version;

            if (!cancelled) {
                onUpdate();
            }
        });

        return {
            cancel: () => {
                cancelled = true;
            },
        };
    }

    /**
     * Wait for an async operation to be resolved.
     */
    private waitUntil(fn: () => Promise<void>) {
        const promise = fn().catch((error) => {
            this.logger.error('Error while waiting for async operation', error);
            throw error;
        });
        if (this.context.waitUntil) {
            this.context.waitUntil(promise);
        }
        this.pending.push(promise);
        promise.finally(() => {
            this.pending.splice(this.pending.indexOf(promise), 1);
        });
    }

    /**
     * Wait for all pending operations to be resolved.
     */
    public async waitPending() {
        await Promise.all(this.pending);
    }
}
